import { defineStore } from "pinia";
import {
  signIn,
  confirmSignIn,
  signOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { fetchCurrentUser } from "@/services/auth/auth.service.js";
import { toastService } from "@/services/toast.service.js";
import {
  clearAuthStorage,
  getStoredUserEmail,
  markAuthEnvironment,
  purgeForeignEnvironmentArtifacts,
  readStoredCurrentUser,
  setAuthItem,
} from "@/utils/authStorage.js";
import {
  readEmailFromSession,
  sessionHasTokens,
  sessionMatchesEnvironment,
} from "@/utils/authSession.js";
import { normalizeUserRole } from "@/utils/userIntelligence.js";
import { getAuthErrorMessage } from "@/utils/authErrors.js";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    currentUser: null,
    email: "",
    isLoading: false,
    otpSent: false,
    sessionReady: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.currentUser?.user_id,
    isSuperAdmin: (state) =>
      normalizeUserRole(state.currentUser?.user_role) === "SUPER_ADMIN",
    isAdmin: (state) =>
      normalizeUserRole(state.currentUser?.user_role) === "ADMIN",
    /** Super admins only — add, edit, remove users */
    canManageUsers: (state) =>
      normalizeUserRole(state.currentUser?.user_role) === "SUPER_ADMIN",
    /** Super admins and admins — manage orgs, SIP, settings, etc. */
    canManagePlatform: (state) => {
      const role = normalizeUserRole(state.currentUser?.user_role);
      return role === "SUPER_ADMIN" || role === "ADMIN";
    },
    userId: (state) => state.currentUser?.user_id ?? null,
  },

  actions: {
    async initSession() {
      purgeForeignEnvironmentArtifacts();

      let session = null;
      try {
        session = await fetchAuthSession();
      } catch {
        session = null;
      }

      if (!sessionHasTokens(session) || !sessionMatchesEnvironment(session)) {
        await this.clearLocalAuth({ signOutCognito: sessionHasTokens(session) });
        this.sessionReady = true;
        return;
      }

      const sessionEmail = readEmailFromSession(session);
      const cachedEmail = getStoredUserEmail();
      const email = sessionEmail || cachedEmail;

      if (!email) {
        await this.clearLocalAuth({ signOutCognito: true });
        this.sessionReady = true;
        return;
      }

      this.email = email;

      const cachedUser = readStoredCurrentUser();
      if (cachedUser?.user_id && cachedEmail === email) {
        this.currentUser = cachedUser;
      }

      const response = await fetchCurrentUser(email);
      if (response?.status === "SUCCESS" && response?.data?.items?.length) {
        this.setUser(response.data.items[0]);
        markAuthEnvironment();
      } else {
        await this.clearLocalAuth({ signOutCognito: true });
      }

      this.sessionReady = true;
    },

    setUser(user) {
      this.currentUser = user;
      if (user?.user_id) {
        setAuthItem("user_id", user.user_id);
        setAuthItem("currentUser", JSON.stringify(user));
        setAuthItem("isLoggedIn", "yes");
        if (user.user_email_id) {
          this.email = String(user.user_email_id).trim().toLowerCase();
          setAuthItem("user_email_id", this.email);
        }
        markAuthEnvironment();
      }
    },

    async sendOtp(email) {
      if (this.isLoading) return false;

      const normalized = String(email || "").trim().toLowerCase();
      if (!normalized) {
        toastService.error("Please enter a valid email");
        return false;
      }

      this.isLoading = true;
      this.email = normalized;

      try {
        const user = await signIn({
          username: normalized,
          options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
        });

        if (user.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
          this.otpSent = true;
          setAuthItem("user_email_id", normalized);
          toastService.success(`Verification code sent to ${normalized}`);
          return true;
        }
      } catch (error) {
        if (error.message === "There is already a signed in user.") {
          await signOut({ global: false });
          return this.sendOtp(normalized);
        }
        toastService.error(getAuthErrorMessage(error, "Failed to send verification code"));
      } finally {
        this.isLoading = false;
      }
      return false;
    },

    async verifyOtp(otp) {
      if (this.isLoading) return false;
      if (!otp || otp.length !== 6) return false;

      this.isLoading = true;
      try {
        await confirmSignIn({ challengeResponse: otp });
        try {
          await fetchAuthSession({ forceRefresh: true });
        } catch {
          /* non-fatal */
        }

        const response = await fetchCurrentUser(this.email);
        if (response?.status !== "SUCCESS" || !response?.data?.items?.length) {
          toastService.error(
            getAuthErrorMessage(response, "Failed to load profile"),
          );
          return false;
        }

        this.setUser(response.data.items[0]);
        toastService.success("Welcome back");
        return true;
      } catch (error) {
        toastService.error(getAuthErrorMessage(error, "Verification failed"));
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    resetOtpFlow() {
      this.otpSent = false;
    },

    async clearLocalAuth({ signOutCognito = false } = {}) {
      if (signOutCognito) {
        try {
          await signOut({ global: false });
        } catch {
          /* continue clearing local session */
        }
      }

      clearAuthStorage();
      this.currentUser = null;
      this.email = "";
      this.otpSent = false;
    },

    async logout() {
      await this.clearLocalAuth({ signOutCognito: true });
    },
  },
});
